import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Card from 'components/UI/Card';
import AssignSoftwareModal from '../AssignSoftwareModal';
import { CardHeaderTop, CardHeaderTopColumn } from 'pages/Connectivity/styled';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import Button from 'components/UI/Button';
import SoftwareHistoryTable from '../SoftwareHistoryTable';
import Pagination, { Page, PagePagination } from 'components/UI/Pagination';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import Loader from 'components/UI/Loader';
import {
  fetchSoftwareUpdates,
  fetchSoftwareUpdatesFromPrevPage as fetchSoftwareUpdatesFromStoredPage,
  softwareUpdateSelector,
} from 'store/softwareUpdateState/softwareUpdateState';
import { SoftwareUpdate, SoftwareUpdateData } from 'models/softwareUpate';
import { useAccess } from 'hooks/useAccess';
import SoftwareStatusTable from '../SoftwareStatusTable';
import EditModal from 'components/UI/EditModal';

type Props = {
  id: string;
};

// Initial pagination
const initialPagination = {
  limit: 5,
  offset: 0,
  page: 1,
};

const DeviceSoftwareHistoryCard: FC<Props> = ({ id }) => {
  const dispatch = useAppDispatch();
  const {
    softwareUpdates,
    isLoading,
    error,
    totalCount,
    lastEvaluatedId,
    nextOffset,
  }: SoftwareUpdateData = useAppSelector(softwareUpdateSelector);
  const { assignSoftwareAccess } = useAccess();
  useEffect(() => {
    if (id) {
      dispatch(fetchSoftwareUpdates(id, 0, 'first'));
    }
  }, [dispatch, id]);

  // Fetch software updates
  const fetchSoftwareUpdateHistory = useCallback(
    (offset?: number, startKey?: string) => {
      if (id) {
        dispatch(fetchSoftwareUpdates(id, offset ?? 0, startKey));
      }
    },
    [dispatch, id]
  );

  // Fetch software updates in previous page
  const fetchPage = useCallback(
    (currentPage: Page) => {
      if (id) {
        dispatch(
          fetchSoftwareUpdatesFromStoredPage(
            currentPage.data,
            totalCount,
            currentPage.lastId,
            currentPage.nextOffset
          )
        );
      }
    },
    [dispatch, totalCount, id]
  );

  const [pagination, setPagination] =
    useState<PagePagination>(initialPagination);
  const [openSoftwareUpdateModal, setOpenSoftwareUpdateModal] =
    useState<boolean>(false);
  const [openDetailsModal, setOpenDetailsModal] = useState<boolean>(false);
  const [selectedsoftwareUpdate, setSelectedSoftwareUpdate] =
    useState<SoftwareUpdate | null>(null);

  // Paginate
  const onSetPagination = useCallback(
    (paginate: PagePagination) => {
      setPagination(paginate);
      if (paginate.pageToShow) {
        fetchPage(paginate.pageToShow);
      } else {
        fetchSoftwareUpdateHistory(paginate.startOffset, paginate.startId);
      }
    },
    [fetchPage, fetchSoftwareUpdateHistory]
  );
  const onOpen = useCallback(() => {
    setOpenSoftwareUpdateModal(true);
  }, [setOpenSoftwareUpdateModal]);

  const onClose = useCallback(() => {
    setOpenSoftwareUpdateModal(false);
  }, [setOpenSoftwareUpdateModal]);

  const onCloseDetails = useCallback(() => {
    setOpenDetailsModal(false);
  }, [setOpenDetailsModal]);

  // on click
  const onClickRow = useCallback(
    (softwareUpdate: SoftwareUpdate | undefined) => {
      if (!softwareUpdate) {
        setSelectedSoftwareUpdate(null);
        setOpenDetailsModal(false);
      } else {
        setSelectedSoftwareUpdate(softwareUpdate);
        setOpenDetailsModal(true);
      }
    },
    [setSelectedSoftwareUpdate, setOpenDetailsModal]
  );

  const statusTable = useMemo(() => {
    if (!selectedsoftwareUpdate) {
      return null;
    }

    return (
      <SoftwareStatusTable
        statusResports={selectedsoftwareUpdate.attributes.statusReports}
        isLoading={isLoading}
      ></SoftwareStatusTable>
    );
  }, [selectedsoftwareUpdate, isLoading]);

  const softwareHistoryTable = useMemo(() => {
    let updatedPagination = pagination;
    if (isLoading) {
      return <Loader center />;
    }
    if (error) {
      return <Heading type={HeadingType.h4}>{error.title}</Heading>;
    }
    if (!softwareUpdates || softwareUpdates?.length === 0) {
      return null;
    }
    if (lastEvaluatedId || nextOffset > 0) {
      const pages = pagination.pages || [];
      const currentPage = pages.find((page) => page.page === pagination.page);
      if (!currentPage) {
        pages.push({
          page: pagination.page,
          lastId: lastEvaluatedId || '',
          data: softwareUpdates,
          nextOffset,
        });
      }
      updatedPagination = {
        ...pagination,
        pages,
      };
    }
    return (
      <div>
        <SoftwareHistoryTable
          softwareUpdates={softwareUpdates}
          onClickRow={onClickRow}
        />
        <Pagination
          totalCount={totalCount || 0}
          noItems={softwareUpdates ? softwareUpdates.length : 0}
          pagination={updatedPagination}
          setPagination={onSetPagination}
        />
      </div>
    );
  }, [
    softwareUpdates,
    totalCount,
    error,
    isLoading,
    onSetPagination,
    pagination,
    onClickRow,
    lastEvaluatedId,
    nextOffset,
  ]);

  return (
    <Card>
      <CardHeaderTop>
        <CardHeaderTopColumn>
          <Heading type={HeadingType.h3} uppercase>
            <FormattedMessage id="connectivity.software_history" />
          </Heading>
        </CardHeaderTopColumn>
        <CardHeaderTopColumn>
          {assignSoftwareAccess && (
            <Button
              id="assign_software_update_btn"
              backgroundColor="transparent"
              color="primary"
              onClick={onOpen}
            >
              <FormattedMessage id="connectivity.software_history_add" />
            </Button>
          )}
        </CardHeaderTopColumn>
      </CardHeaderTop>
      {softwareHistoryTable}
      <EditModal
        open={openDetailsModal}
        close={onCloseDetails}
        title={'connectivity.software_history_details_header'}
      >
        <Heading type={HeadingType.h3} uppercase>
          <FormattedMessage
            id="connectivity.software_history_details_sub_header"
            values={{
              software: selectedsoftwareUpdate?.attributes.software,
            }}
          />
        </Heading>

        {statusTable}
      </EditModal>
      <AssignSoftwareModal
        iprId={id}
        open={openSoftwareUpdateModal}
        onClose={onClose}
      />
    </Card>
  );
};

export default DeviceSoftwareHistoryCard;
